import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'es' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  es: {
    'nav.home': 'Treid',
    'nav.homeSub': 'Inicio',
    'nav.aboutCompany': 'Nuestra Empresa',
    'nav.intelligence': 'Inteligencia',
    'nav.compliance': 'Cumplimiento',
    'nav.markets': 'Mercados',
    'nav.events': 'Eventos',
    'nav.login': 'Iniciar Sesión',
    'nav.consultancy': 'Agendar Consultoría',
    'nav.profile': 'Mi Perfil',
    'nav.logout': 'Cerrar Sesión',
    'footer.description': 'La plataforma líder en inteligencia y cumplimiento comercial en México.',
    'footer.solutions': 'Soluciones',
    'footer.company': 'Empresa',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacidad de Datos',
    'footer.terms': 'Términos de Comercio',
    'footer.network': 'Red Global',
    'footer.newsletter': 'Boletín Informativo',
    'footer.rights': 'Todos los derechos reservados.',
    'home.hero.titleL1': 'Inteligencia que Transforma el',
    'home.hero.titleL2': 'Comercio Exterior',
    'home.hero.description': 'Desbloqueamos datos estratégicos para optimizar sus operaciones globales. Desde cumplimiento normativo hasta inteligencia de mercados, TREID es el motor de su éxito internacional.',
    'home.hero.cta1': 'Agenda Consultoría',
    'home.hero.cta2': 'Registro a Webinars',
    'home.hero.report': 'Descargar Reporte Actualizado (PDF)',
    'home.intelligence.trend.desc': 'Monitor predictivo de actividad logística en puertos secos y marítimos.',
    'home.intelligence.origin.desc': 'Distribución geográfica de las importaciones del mes en curso.',
    'home.intelligence.clearance.desc': 'Tiempo promedio de liberación en aduanas nacionales.',
    'home.stats.mxn': 'USD / MXN',
    'home.stats.freight': 'WCI FREIGHT INDEX',
    'home.stats.shanghai': 'SHANGHAI CONTAINER',
    'home.stats.demand': 'DEMANDA LOGÍSTICA',
    'home.stats.optimal': 'ÓPTIMO',
    'home.solutions.label': 'Nuestras Soluciones',
    'home.solutions.title': 'Ecosistema de Servicios Integrales',
    'home.solutions.viewAll': 'Ver todos los servicios',
    'home.solutions.explore': 'Explorar Servicio',
    'home.podcast.label': 'TREID PODCAST',
    'home.podcast.title': 'Treid Comercio Exterior',
    'home.podcast.description': 'Escucha nuestro podcast oficial sobre comercio exterior, aduanas y logística internacional. Mantente al día con análisis de expertos y tendencias del mercado global.',
    'home.podcast.listen_spotify': 'Escuchar en Spotify',
    'home.podcast.listen_apple': 'Escuchar en Apple Podcasts',
    'home.intelligence.label': 'Plataforma Digital',
    'home.intelligence.title': 'Acceso Instantáneo a la Autoridad Arancelaria',
    'home.intelligence.description': 'Nuestra herramienta propietaria permite búsquedas precisas de fracciones arancelarias, requisitos técnicos y tendencias de importación en tiempo real.',
    'home.intelligence.searchPlaceholder': 'Buscar fracción o producto...',
    'home.intelligence.analytics': 'TREID ANALYTICS v.4.0',
    'home.intelligence.trend': 'Tendencia de Volumen de Importación',
    'home.intelligence.origin': 'Origen Principal',
    'home.intelligence.clearance': 'Despacho Promedio',
    'home.intelligence.days': 'Días',
    'intel.title': 'Centro de Inteligencia Comercial',
    'intel.subtitle': 'Visualización en tiempo real de flujos comerciales, indicadores financieros y marcos regulatorios para la toma de decisiones estratégicas.',
    'intel.searchPlaceholder': 'Buscar por HS Code, Producto o Puerto...',
    'intel.advancedFilters': 'Filtros Avanzados',
    'intel.exchangeRates': 'Tipos de Cambio en Tiempo Real (vs MXN)',
    'intel.liveMarket': 'Mercado en Vivo',
    'intel.importTrends': 'Panel de Tendencias de Importación',
    'intel.trend.automotive': 'Automotriz y Partes',
    'intel.trend.electronics': 'Electrónica',
    'intel.trend.agriculture': 'Agricultura',
    'intel.monthly': 'Mensual',
    'intel.quarterly': 'Trimestral',
    'intel.tariffCalculator': 'Calculadora de Aranceles',
    'intel.customsValue': 'Valor en Aduana (USD)',
    'intel.duty': 'IGA / Arancel (%)',
    'intel.estimatedTax': 'Impuesto Estimado',
    'intel.calculate': 'Calcular Estimación',
    'intel.hsSearch': 'Buscador de Fracciones',
    'intel.hsPlaceholder': 'Ej: 8703.23.01',
    'intel.hsInstructions': 'Ingrese el código de 8 o 10 dígitos para obtener regulaciones vigentes y notas explicativas del Sistema Armonizado.',
    'intel.logisticsLabel': 'Indicadores Logísticos',
    'intel.logistic.port': 'Saturación de Puerto (Manzanillo)',
    'intel.logistic.freight': 'Índice de Carga Terrestre',
    'intel.logistic.critical': 'Crítico',
    'intel.logistic.stable': 'Estable',
    'intel.alertsTitle': 'Feed de Alertas Regulatorias',
    'intel.viewAllAlerts': 'Ver Todas las Notificaciones',
    'intel.alert1.msg': 'Modificación al Anexo 2.4.1 referente al etiquetado de eficiencia energética para equipos electrónicos.',
    'intel.alert2.msg': 'Aviso de saturación temporal en el Puente del Comercio Mundial debido a fallas técnicas en el sistema VUCEM.',
    'intel.alert3.msg': 'Nueva resolución sobre la Regla 3.1.25 para el despacho de mercancías sensibles en recintos fiscalizados.',
    'intel.trend.desc': 'Análisis del volumen total de importaciones basado en pedimentos procesados. Los sectores estratégicos muestran la tracción del Nearshoring en territorio nacional.',
    'intel.hsResult.title': 'Resultado de Búsqueda de Fracción',
    'intel.hsResult.code': 'Fracción Arancelaria',
    'intel.hsResult.duty': 'Arancel IGI',
    'intel.hsResult.regs': 'RRNA (Regulaciones y Restricciones No Arancelarias)',
    'intel.hsResult.tips': 'Recomendación Treid',
    'intel.calc.result': 'Total Impuestos (Estimado)',
    'comp.elite': 'SERVICIOS DE ÉLITE',
    'comp.title': 'Consultoría en Comercio Exterior & Compliance.',
    'comp.subtitle': 'Blindamos sus operaciones internacionales mediante auditoría preventiva y arquitectura de cumplimiento normativo de alto nivel.',
    'comp.cta1': 'Solicitar Diagnóstico Preliminar',
    'comp.cta2': 'Ver Metodología',
    'comp.riskTitle': 'El riesgo de la invisibilidad normativa.',
    'comp.riskDesc': 'Las empresas enfrentan sanciones millonarias y bloqueos logísticos no por falta de voluntad, sino por la complejidad técnica de un ecosistema legal en constante cambio.',
    'comp.pain1': 'El 70% de las empresas no detectan discrepancias en sus expedientes hasta que llega una auditoría del SAT.',
    'comp.pain2': 'La clasificación arancelaria incorrecta es la principal causa de multas innecesarias en México.',
    'comp.pain3': 'La falta de trazabilidad en el Anexo 24 pone en riesgo la permanencia del programa IMMEX.',
    'comp.failStat': 'Empresas con fallas en compliance',
    'comp.costStat': 'Costo promedio de sanciones (USD)',
    'comp.process': 'NUESTRO PROCESO',
    'comp.methodologyTitle': 'Metodología Treider',
    'comp.authority': 'Autoridad probada en mercados globales.',
    'comp.advantage': 'Transformamos el cumplimiento de un centro de costos a una ventaja competitiva estratégica.',
    'comp.methodology.01.title': 'Diagnóstico',
    'comp.methodology.01.desc': 'Escaneo profundo de archivos electrónicos (Data Stage) para identificar discrepancias históricas.',
    'comp.methodology.01.item1': 'Auditoría Preventiva',
    'comp.methodology.01.item2': 'Análisis de Riesgos',
    'comp.methodology.02.title': 'Implementación',
    'comp.methodology.02.desc': 'Despliegue de arquitecturas de cumplimiento y automatización de procesos para garantizar el orden.',
    'comp.methodology.02.item1': 'Manuales de Compliance',
    'comp.methodology.02.item2': 'Integración Tecnológica',
    'comp.methodology.03.title': 'Seguimiento',
    'comp.methodology.03.desc': 'Monitoreo constante y actualización ante cambios en leyes aduaneras y tratados internacionales.',
    'comp.methodology.03.item1': 'Dashboard de Control',
    'comp.methodology.03.item2': 'Soporte 24/7',
    'comp.painpoint.01.title': 'Auditorías Fiscales',
    'comp.painpoint.01.desc': 'Errores en pedimentos que derivan en multas por omisión de impuestos.',
    'comp.painpoint.02.title': 'Suspensión de Padrones',
    'comp.painpoint.02.desc': 'El riesgo inminente de detener la operación por incumplimiento administrativo.',
    'comp.testimonial.01.text': '"Redujimos nuestro tiempo de despacho en un 30% tras implementar la arquitectura de compliance de TREID."',
    'comp.testimonial.01.author': 'Director de Logística',
    'comp.testimonial.01.company': 'Consorcio Automotriz Global',
    'comp.testimonial.02.text': '"Su auditoría Data Stage nos salvó de una multa potencial de 1.2M de USD por errores en clasificaciones arancelarias."',
    'comp.testimonial.02.author': 'Legal & Compliance Lead',
    'comp.testimonial.02.company': 'Distribuidora Internacional',
    'comp.calcTitle': 'Calculadora de Nivel de Riesgo',
    'comp.calcDesc': 'Complete el formulario para recibir un scoring preliminar de su estatus ante autoridades aduaneras.',
    'comp.calc.rfc': 'RFC',
    'comp.calc.name': 'Nombre Completo',
    'comp.calc.company': 'Empresa',
    'comp.calc.volMonthly': 'Volumen Mensual',
    'comp.calc.concern': 'Preocupación Principal',
    'comp.calc.message': 'Mensaje / Detalle',
    'comp.calc.placeholder.name': 'Escriba su nombre',
    'comp.calc.placeholder.company': 'Nombre de la razón social',
    'comp.calc.placeholder.message': 'Describa brevemente su situación',
    'comp.calc.opt.vol1': '1 - 50 Pedimentos',
    'comp.calc.opt.vol2': '51 - 200 Pedimentos',
    'comp.calc.opt.vol3': '201+ Pedimentos',
    'comp.calc.opt.concern1': 'Auditoría de Documentos',
    'comp.calc.opt.concern2': 'Compliance Normativo',
    'comp.calc.opt.concern3': 'Otros',
    'comp.calc.sector': 'Sector Industrial',
    'comp.calc.volume': 'Volumen de Importación Anual',
    'comp.calc.ops': 'Operaciones Mensuales',
    'comp.calc.placeholder.volume': 'Selecciona volumen...',
    'comp.calc.vol1': 'Menos de $1M USD',
    'comp.calc.vol2': '$1M - $10M USD',
    'comp.calc.vol3': 'Más de $10M USD',
    'comp.calcSubmit': 'Obtener Diagnóstico Express',
    'markets.featured': 'Destacado: Mercados',
    'markets.featured.title': 'Nearshoring: La Reconfiguración de las Cadenas de Suministro en México',
    'markets.featured.desc': 'Análisis profundo sobre el impacto de la inversión extranjera directa y las oportunidades para el sector manufacturero.',
    'markets.read': 'Leer Artículo',
    'markets.minRead': 'MIN DE LECTURA',
    'markets.allInsights': 'Todos los Insights',
    'markets.viewAll': 'VER TODO',
    'markets.editorial': 'Centro Editorial',
    'markets.cat.aduanas': 'Aduanas',
    'markets.cat.noms': 'NOMs',
    'markets.cat.nearshoring': 'Nearshoring',
    'markets.cat.compliance': 'Cumplimiento',
    'markets.feed.item1.title': 'Nuevas disposiciones del SAT para la exportación de productos agroindustriales.',
    'markets.feed.item2.title': 'El rol de la digitalización en el despacho aduanero mexicano: Hacia un sistema sin papeles.',
    'markets.feed.item3.title': 'Análisis de impacto: La relocalización de inversiones (nearshoring) en el corredor norte.',
    'markets.hubTitle': 'Únete al HUB de Comunicación',
    'markets.hubDesc': 'Recibe análisis exclusivos de comercio exterior y actualizaciones regulatorias directamente en tu inbox.',
    'markets.subscribe': 'SUSCRIBIRSE AL HUB',
    'markets.acceptTerms': 'Al suscribirte aceptas nuestros Términos de Comercio y Privacidad.',
    'events.title': 'Eventos y',
    'events.webinars': 'Webinars',
    'events.community': 'Comunidad Treid',
    'events.subtitle': 'Únete a la red de expertos en comercio exterior más activa de México. Aprende, conecta y crece con Treid.',
    'events.list': 'Lista',
    'events.calendar': 'Calendario',
    'events.all': 'Todos',
    'events.myEvents': 'Mis Eventos',
    'events.slots': 'Lugares',
    'events.register': 'Registrarme',
    'events.registered': 'Registrado',
    'events.noEvents': 'No se encontraron eventos en esta categoría',
    'events.dateTime': 'Fecha y Hora',
    'events.location': 'Ubicación',
    'events.about': 'Sobre el evento',
    'events.speakers': 'Ponentes',
    'events.type.inperson': 'Presencial',
    'events.type.webinar': 'Webinar',
    'events.confirm': 'Confirmar Registro',
    'events.viewInGoogle': 'Ver en Google Calendar',
    'gt.activate': 'Activa tu Golden Ticket',
    'gt.subtitle': 'Diagnóstico táctico de 30 minutos con un experto senior. Una llave por RFC.',
    'gt.step': 'Paso',
    'gt.of': 'de',
    'gt.completed': 'completado',
    'gt.identity': 'Identidad y Validación',
    'gt.identityDesc': 'Validamos la unicidad de tu Golden Ticket.',
    'gt.field.rfc': 'RFC de la Empresa',
    'gt.field.sector': 'Sector Industrial',
    'gt.field.ops': '¿Cuántas operaciones realizas al mes?',
    'gt.field.challenge': 'Describe tu mayor reto de hoy',
    'gt.field.details': 'Detalles adicionales',
    'gt.placeholder.select': 'Selecciona una opción...',
    'gt.placeholder.challenge': 'Ej: Clasificación de nuevas tecnologías...',
    'gt.profile': 'Perfil de la Operación',
    'gt.profileDesc': 'Cuéntanos sobre tu actividad comercial.',
    'gt.diag': 'Diagnóstico del Reto',
    'gt.diagDesc': 'Detalla la problemática que deseas atender.',
    'gt.schedule': 'Agenda tu Sesión',
    'gt.scheduleDesc': 'Finaliza eligiendo el horario de tu preferencia.',
    'gt.verifying': 'Verificando disponibilidad...',
    'gt.available': '¡Ticket disponible para activación!',
    'gt.alreadyActivated': 'Este RFC ya activó su Golden Ticket anteriormente.',
    'gt.requestRegular': 'Solicitar contacto regular',
    'gt.fullName': 'Nombre Completo',
    'gt.role': 'Cargo o Rol',
    'gt.role.placeholder': 'Selecciona...',
    'gt.role.ceo': 'Director / CEO',
    'gt.role.logistics': 'Gerente de Logística',
    'gt.role.coordinator': 'Coordinador de Comercio',
    'gt.role.compliance': 'Analista de Compliance',
    'gt.role.owner': 'Dueño / Fundador',
    'gt.role.other': 'Otro',
    'gt.company': 'Empresa o Proyecto',
    'gt.company.placeholder': 'Nombre de la compañía',
    'gt.email': 'Email corporativo',
    'gt.whatsapp': 'WhatsApp / Teléfono',
    'gt.sector': 'Sector Industrial',
    'gt.state': 'Estado donde opera',
    'gt.opType': 'Tipo de Operación',
    'gt.programs': 'Programas Activos',
    'gt.volume': 'Volumen Mensual',
    'gt.topic': 'Tema Principal',
    'gt.problemDesc': 'Descripción del problema',
    'gt.problemDesc.placeholder': 'Describe el reto en al menos 200 caracteres...',
    'gt.urgency': 'Urgencia',
    'gt.source': '¿Cómo te enteraste?',
    'gt.source.book': 'Libro "El Manual Prohibido"',
    'gt.source.linkedin': 'LinkedIn',
    'gt.source.referral': 'Recomendación',
    'gt.scheduleTitle': 'Mayo 2024',
    'gt.availableHours': 'Horarios Disponibles',
    'gt.modal': 'Modalidad',
    'gt.modal.video': 'Video',
    'gt.modal.phone': 'Llamada',
    'gt.consent.privacy': 'He leído y acepto el Aviso de Privacidad conforme a la legislación vigente.',
    'gt.consent.marketing': 'Deseo recibir información relevante sobre comercio exterior y actualizaciones normativas.',
    'gt.consent.terms': 'Acepto los Términos del Golden Ticket: sesión única de 30 min por RFC.',
    'gt.back': 'Atrás',
    'gt.continue': 'Continuar',
    'gt.confirm': 'Confirmar Activación',
    'article.back': 'Volver a Markets',
    'article.related': 'Expertise Relacionado',
    'article.subscribe': 'Suscríbete al Hub de Comunicación',
    'article.subscribeBtn': 'Suscribirme',
    'article.subscribeDesc': 'Recibe análisis detallados antes que nadie.',
    'article.audit': 'Auditoría Preventiva',
    'article.tariff': 'Análisis Arancelario',
    'article.schedule': 'Agenda Consultoría',
    'auth.welcome': 'Bienvenido al futuro del Comercio Exterior.',
    'auth.description': 'Únete a la plataforma líder en inteligencia y compliance comercial en México.',
    'auth.login': 'Iniciar Sesión',
    'auth.register': 'Crear Cuenta',
    'auth.google': 'Continuar con Google',
    'auth.or': 'O con tu cuenta Treid',
    'auth.noAccount': '¿No tienes cuenta?',
    'auth.hasAccount': 'Ya tengo cuenta',
    'auth.completeProfile': 'Completa tu Perfil',
    'auth.needDetails': 'Necesitamos unos últimos detalles para personalizar tu experiencia.',
    'auth.displayName': 'Nombre Completo',
    'auth.email': 'Correo Electrónico',
    'auth.password': 'Contraseña',
    'auth.passwordPlaceholder': '••••••••',
    'auth.company': 'Empresa',
    'auth.industry': 'Industria',
    'auth.interest': 'Interés Principal',
    'auth.interestPlaceholder': 'Selecciona uno...',
    'auth.interest.intelligence': 'Inteligencia Comercial',
    'auth.interest.compliance': 'Compliance Aduanero',
    'auth.interest.events': 'Eventos y Networking',
    'auth.interest.consultancy': 'Consultoría Estratégica',
    'auth.processing': 'Procesando...',
    'auth.finish': 'Finalizar Registro',
    'auth.registerFree': 'Regístrate gratis',
    'admin.blog.title': 'Editor de Blog',
    'admin.blog.panel': 'Panel de Administración',
    'admin.blog.cancel': 'Cancelar',
    'admin.blog.config': 'Configuración',
    'admin.blog.field.title': 'Título del Artículo',
    'admin.blog.field.content': 'Contenido (Editor Enriquecido)',
    'admin.blog.field.lang': 'Idioma del Artículo',
    'admin.blog.field.slug': 'Slug (URL)',
    'admin.blog.field.cat': 'Categoría',
    'admin.blog.field.author': 'Autor',
    'admin.blog.field.img': 'URL de Imagen de Portada',
    'admin.blog.publish': 'Publicar Artículo',
    'admin.blog.publishing': 'Publicando...',
    'admin.blog.success.title': '¡Artículo Publicado!',
    'admin.blog.success.desc': 'Redirigiendo a la sección de noticias...',
    'admin.blog.denied.title': 'Acceso Denegado',
    'admin.blog.denied.desc': 'Solo los administradores pueden acceder a esta página.',
    'admin.blog.denied.back': 'Volver al inicio',
  },
  en: {
    'nav.home': 'Treid',
    'nav.homeSub': 'Home',
    'nav.aboutCompany': 'Our Company',
    'nav.intelligence': 'Intelligence',
    'nav.compliance': 'Compliance',
    'nav.markets': 'Markets',
    'nav.events': 'Events',
    'nav.login': 'Login',
    'nav.consultancy': 'Book Consultancy',
    'nav.profile': 'My Profile',
    'nav.logout': 'Logout',
    'footer.description': 'The leading platform for intelligence and commercial compliance in Mexico.',
    'footer.solutions': 'Solutions',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.privacy': 'Data Privacy',
    'footer.terms': 'Terms of Trade',
    'footer.network': 'Global Network',
    'footer.newsletter': 'Newsletter',
    'footer.rights': 'All rights reserved.',
    'home.hero.titleL1': 'Intelligence that Transforms',
    'home.hero.titleL2': 'Foreign Trade',
    'home.hero.description': 'We unlock strategic data to optimize your global operations. From regulatory compliance to market intelligence, TREID is the engine of your international success.',
    'home.hero.cta1': 'Book Consultancy',
    'home.hero.cta2': 'Webinar Registration',
    'home.hero.report': 'Download Updated Report (PDF)',
    'home.intelligence.trend.desc': 'Predictive monitor of logistics activity in dry and maritime ports.',
    'home.intelligence.origin.desc': 'Geographical distribution of imports for the current month.',
    'home.intelligence.clearance.desc': 'Average release time in national customs.',
    'home.stats.mxn': 'USD / MXN',
    'home.stats.freight': 'WCI FREIGHT INDEX',
    'home.stats.shanghai': 'SHANGHAI CONTAINER',
    'home.stats.demand': 'LOGISTICS DEMAND',
    'home.stats.optimal': 'OPTIMAL',
    'home.solutions.label': 'Our Solutions',
    'home.solutions.title': 'Comprehensive Service Ecosystem',
    'home.solutions.viewAll': 'View all services',
    'home.solutions.explore': 'Explore Service',
    'home.podcast.label': 'TREID PODCAST',
    'home.podcast.title': 'Treid Foreign Trade Podcast',
    'home.podcast.description': 'Listen to our official podcast on foreign trade, customs, and international logistics. Stay updated with expert analysis and global market trends.',
    'home.podcast.listen_spotify': 'Listen on Spotify',
    'home.podcast.listen_apple': 'Listen on Apple Podcasts',
    'home.intelligence.label': 'Digital Platform',
    'home.intelligence.title': 'Instant Access to Tariff Authority',
    'home.intelligence.description': 'Our proprietary tool allows precise searches for tariff fractions, technical requirements, and real-time import trends.',
    'home.intelligence.searchPlaceholder': 'Search fraction or product...',
    'home.intelligence.analytics': 'TREID ANALYTICS v.4.0',
    'home.intelligence.trend': 'Import Volume Trend',
    'home.intelligence.origin': 'Main Origin',
    'home.intelligence.clearance': 'Avg. Clearance',
    'home.intelligence.days': 'Days',
    'intel.title': 'Trade Intelligence Center',
    'intel.subtitle': 'Real-time visualization of commercial flows, financial indicators, and regulatory frameworks for strategic decision-making.',
    'intel.searchPlaceholder': 'Search by HS Code, Product or Port...',
    'intel.advancedFilters': 'Advanced Filters',
    'intel.exchangeRates': 'Real-time Exchange Rates (vs MXN)',
    'intel.liveMarket': 'Live Market',
    'intel.importTrends': 'Import Trends Dashboard',
    'intel.trend.automotive': 'Automotive & Parts',
    'intel.trend.electronics': 'Electronics',
    'intel.trend.agriculture': 'Agriculture',
    'intel.monthly': 'Monthly',
    'intel.quarterly': 'Quarterly',
    'intel.tariffCalculator': 'Tariff Calculator',
    'intel.customsValue': 'Customs Value (USD)',
    'intel.duty': 'IGA / Duty (%)',
    'intel.estimatedTax': 'Estimated Tax',
    'intel.calculate': 'Calculate Estimation',
    'intel.hsSearch': 'HS Code Search',
    'intel.hsPlaceholder': 'Ex: 8703.23.01',
    'intel.hsInstructions': 'Enter the 8 or 10-digit code to obtain current regulations and explanatory notes from the Harmonized System.',
    'intel.logisticsLabel': 'Logistics Indicators',
    'intel.logistic.port': 'Port Saturation (Manzanillo)',
    'intel.logistic.freight': 'Inland Freight Index',
    'intel.logistic.critical': 'Critical',
    'intel.logistic.stable': 'Stable',
    'intel.alertsTitle': 'Regulatory Alerts Feed',
    'intel.viewAllAlerts': 'View All Notifications',
    'intel.alert1.msg': 'Modification to Annex 2.4.1 regarding energy efficiency labeling for electronic equipment.',
    'intel.alert2.msg': 'Notice of temporary saturation at the World Trade Bridge due to technical failures in the VUCEM system.',
    'intel.alert3.msg': 'New resolution on Rule 3.1.25 for the clearance of sensitive goods in bonded warehouses.',
    'intel.trend.desc': 'Analysis of total import volume based on processed customs entries. Strategic sectors show the traction of Nearshoring in national territory.',
    'intel.hsResult.title': 'Tariff Fraction Search Result',
    'intel.hsResult.code': 'HS Code / Fraction',
    'intel.hsResult.duty': 'IGI Duty',
    'intel.hsResult.regs': 'NTR (Non-Tariff Regulations)',
    'intel.hsResult.tips': 'Treid Recommendation',
    'intel.calc.result': 'Total Estimated Taxes',
    'comp.elite': 'ELITE SERVICES',
    'comp.title': 'Foreign Trade Consulting & Compliance.',
    'comp.subtitle': 'We shield your international operations through preventive auditing and high-level regulatory compliance architecture.',
    'comp.cta1': 'Request Preliminary Diagnosis',
    'comp.cta2': 'View Methodology',
    'comp.riskTitle': 'The risk of regulatory invisibility.',
    'comp.riskDesc': 'Companies face millionaire sanctions and logistical blockades not out of lack of will, but due to the technical complexity of a constantly changing legal ecosystem.',
    'comp.pain1': '70% of companies do not detect discrepancies in their records until a SAT audit arrives.',
    'comp.pain2': 'Incorrect tariff classification is the leading cause of unnecessary fines in Mexico.',
    'comp.pain3': 'Lack of traceability in Annex 24 puts the permanence of the IMMEX program at risk.',
    'comp.failStat': 'Companies with compliance failures',
    'comp.costStat': 'Average cost of sanctions (USD)',
    'comp.process': 'OUR PROCESS',
    'comp.methodologyTitle': 'Treider Methodology',
    'comp.authority': 'Proven authority in global markets.',
    'comp.advantage': 'We transform compliance from a cost center to a strategic competitive advantage.',
    'comp.methodology.01.title': 'Diagnosis',
    'comp.methodology.01.desc': 'Deep scan of electronic files (Data Stage) to identify historical discrepancies.',
    'comp.methodology.01.item1': 'Preventive Audit',
    'comp.methodology.01.item2': 'Risk Analysis',
    'comp.methodology.02.title': 'Implementation',
    'comp.methodology.02.desc': 'Deployment of compliance architectures and process automation to guarantee order.',
    'comp.methodology.02.item1': 'Compliance Manuals',
    'comp.methodology.02.item2': 'Technological Integration',
    'comp.methodology.03.title': 'Follow-up',
    'comp.methodology.03.desc': 'Constant monitoring and updating before changes in customs laws and international treaties.',
    'comp.methodology.03.item1': 'Control Dashboard',
    'comp.methodology.03.item2': '24/7 Support',
    'comp.painpoint.01.title': 'Tax Audits',
    'comp.painpoint.01.desc': 'Errors in customs entries resulting in fines for tax omission.',
    'comp.painpoint.02.title': 'Importer Registry Suspension',
    'comp.painpoint.02.desc': 'The imminent risk of stopping the operation due to administrative non-compliance.',
    'comp.testimonial.01.text': '"We reduced our dispatch time by 30% after implementing TREID\'s compliance architecture."',
    'comp.testimonial.01.author': 'Logistics Director',
    'comp.testimonial.01.company': 'Global Automotive Consortium',
    'comp.testimonial.02.text': '"Your Data Stage audit saved us from a potential $1.2M USD fine for errors in tariff classifications."',
    'comp.testimonial.02.author': 'Legal & Compliance Lead',
    'comp.testimonial.02.company': 'International Distributor',
    'comp.calcTitle': 'Risk Level Calculator',
    'comp.calcDesc': 'Fill out the form to receive a preliminary scoring of your status with customs authorities.',
    'comp.calc.rfc': 'RFC',
    'comp.calc.name': 'Full Name',
    'comp.calc.company': 'Company',
    'comp.calc.volMonthly': 'Monthly Volume',
    'comp.calc.concern': 'Main Concern',
    'comp.calc.message': 'Message / Detail',
    'comp.calc.placeholder.name': 'Enter your name',
    'comp.calc.placeholder.company': 'Business name',
    'comp.calc.placeholder.message': 'Briefly describe your situation',
    'comp.calc.opt.vol1': '1 - 50 Customs entries',
    'comp.calc.opt.vol2': '51 - 200 Customs entries',
    'comp.calc.opt.vol3': '201+ Customs entries',
    'comp.calc.opt.concern1': 'Document Audit',
    'comp.calc.opt.concern2': 'Regulatory Compliance',
    'comp.calc.opt.concern3': 'Others',
    'comp.calc.sector': 'Industrial Sector',
    'comp.calc.volume': 'Annual Import Volume',
    'comp.calc.ops': 'Monthly Operations',
    'comp.calc.placeholder.volume': 'Select volume...',
    'comp.calc.vol1': 'Less than $1M USD',
    'comp.calc.vol2': '$1M - $10M USD',
    'comp.calc.vol3': 'More than $10M USD',
    'comp.calcSubmit': 'Get Express Diagnosis',
    'markets.featured': 'Featured: Markets',
    'markets.featured.title': 'Nearshoring: The Reconfiguration of Supply Chains in Mexico',
    'markets.featured.desc': 'Deep analysis of the impact of foreign direct investment and opportunities for the manufacturing sector.',
    'markets.read': 'Read Article',
    'markets.minRead': 'MIN READ',
    'markets.allInsights': 'All Insights',
    'markets.viewAll': 'VIEW ALL',
    'markets.editorial': 'Editorial Center',
    'markets.cat.aduanas': 'Customs',
    'markets.cat.noms': 'NOMs',
    'markets.cat.nearshoring': 'Nearshoring',
    'markets.cat.compliance': 'Compliance',
    'markets.feed.item1.title': 'New SAT provisions for the export of agro-industrial products.',
    'markets.feed.item2.title': 'The role of digitalization in Mexican customs clearance: Towards a paperless system.',
    'markets.feed.item3.title': 'Impact analysis: The relocation of investments (nearshoring) in the northern corridor.',
    'markets.hubTitle': 'Join the Communication HUB',
    'markets.hubDesc': 'Receive exclusive foreign trade analysis and regulatory updates directly in your inbox.',
    'markets.subscribe': 'SUBSCRIBE TO HUB',
    'markets.acceptTerms': 'By subscribing you accept our Trade Terms and Privacy.',
    'events.title': 'Events &',
    'events.webinars': 'Webinars',
    'events.community': 'Treid Community',
    'events.subtitle': "Join Mexico's most active foreign trade expert network. Learn, connect and grow with Treid.",
    'events.list': 'List',
    'events.calendar': 'Calendar',
    'events.all': 'All',
    'events.myEvents': 'My Events',
    'events.slots': 'Slots',
    'events.register': 'Register',
    'events.registered': 'Registered',
    'events.noEvents': 'No events found in this category',
    'events.dateTime': 'Date & Time',
    'events.location': 'Location',
    'events.about': 'About the event',
    'events.speakers': 'Speakers',
    'events.type.inperson': 'In-person',
    'events.type.webinar': 'Webinar',
    'events.confirm': 'Confirm Registration',
    'events.viewInGoogle': 'View in Google Calendar',
    'gt.activate': 'Activate your Golden Ticket',
    'gt.subtitle': '30-minute tactical diagnosis with a senior expert. One key per RFC.',
    'gt.step': 'Step',
    'gt.of': 'of',
    'gt.completed': 'completed',
    'gt.identity': 'Identity and Validation',
    'gt.identityDesc': 'We validate the uniqueness of your Golden Ticket.',
    'gt.field.rfc': 'Company RFC',
    'gt.field.sector': 'Industrial Sector',
    'gt.field.ops': 'How many operations do you perform per month?',
    'gt.field.challenge': 'Describe your biggest challenge today',
    'gt.field.details': 'Additional details',
    'gt.placeholder.select': 'Select an option...',
    'gt.placeholder.challenge': 'Ex: Classification of new technologies...',
    'gt.profile': 'Operation Profile',
    'gt.profileDesc': 'Tell us about your business activity.',
    'gt.diag': 'Challenge Diagnosis',
    'gt.diagDesc': 'Detail the problematic you want to address.',
    'gt.schedule': 'Schedule your Session',
    'gt.scheduleDesc': 'Finish by choosing your preferred time.',
    'gt.verifying': 'Verifying availability...',
    'gt.available': 'Ticket available for activation!',
    'gt.alreadyActivated': 'This RFC has already activated its Golden Ticket previously.',
    'gt.requestRegular': 'Request regular contact',
    'gt.fullName': 'Full Name',
    'gt.role': 'Title or Role',
    'gt.role.placeholder': 'Select...',
    'gt.role.ceo': 'Director / CEO',
    'gt.role.logistics': 'Logistics Manager',
    'gt.role.coordinator': 'Trade Coordinator',
    'gt.role.compliance': 'Compliance Analyst',
    'gt.role.owner': 'Owner / Founder',
    'gt.role.other': 'Other',
    'gt.company': 'Company or Project',
    'gt.company.placeholder': 'Company name',
    'gt.email': 'Corporate email',
    'gt.whatsapp': 'WhatsApp / Phone',
    'gt.sector': 'Industrial Sector',
    'gt.state': 'State of operation',
    'gt.opType': 'Operation Type',
    'gt.programs': 'Active Programs',
    'gt.volume': 'Monthly Volume',
    'gt.topic': 'Main Topic',
    'gt.problemDesc': 'Problem description',
    'gt.problemDesc.placeholder': 'Describe the challenge in at least 200 characters...',
    'gt.urgency': 'Urgency',
    'gt.source': 'How did you hear about us?',
    'gt.source.book': 'Book "The Forbidden Manual"',
    'gt.source.linkedin': 'LinkedIn',
    'gt.source.referral': 'Recommendation',
    'gt.scheduleTitle': 'May 2024',
    'gt.availableHours': 'Available Times',
    'gt.modal': 'Modality',
    'gt.modal.video': 'Video',
    'gt.modal.phone': 'Call',
    'gt.consent.privacy': 'I have read and accept the Privacy Notice in accordance with current legislation.',
    'gt.consent.marketing': 'I wish to receive relevant information on foreign trade and regulatory updates.',
    'gt.consent.terms': 'I accept the Golden Ticket Terms: single 30 min session per RFC.',
    'gt.back': 'Back',
    'gt.continue': 'Continue',
    'gt.confirm': 'Confirm Activation',
    'article.back': 'Back to Markets',
    'article.related': 'Related Expertise',
    'article.subscribe': 'Subscribe to our Communication Hub',
    'article.subscribeBtn': 'Subscribe',
    'article.subscribeDesc': 'Receive detailed analysis before anyone else.',
    'article.audit': 'Preventive Audit',
    'article.tariff': 'Tariff Analysis',
    'article.schedule': 'Consulting Schedule',
    'auth.welcome': 'Welcome to the future of Foreign Trade.',
    'auth.description': 'Join the leading platform for intelligence and commercial compliance in Mexico.',
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.google': 'Continue with Google',
    'auth.or': 'Or with your Treid account',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account',
    'auth.completeProfile': 'Complete your Profile',
    'auth.needDetails': 'We need a few last details to personalize your experience.',
    'auth.displayName': 'Full Name',
    'auth.email': 'Email Address',
    'auth.password': 'Password',
    'auth.passwordPlaceholder': '••••••••',
    'auth.company': 'Company',
    'auth.industry': 'Industry',
    'auth.interest': 'Main Interest',
    'auth.interestPlaceholder': 'Select one...',
    'auth.interest.intelligence': 'Commercial Intelligence',
    'auth.interest.compliance': 'Customs Compliance',
    'auth.interest.events': 'Events & Networking',
    'auth.interest.consultancy': 'Strategic Consulting',
    'auth.processing': 'Processing...',
    'auth.finish': 'Finish Registration',
    'auth.registerFree': 'Register for free',
    'admin.blog.title': 'Blog Editor',
    'admin.blog.panel': 'Administration Panel',
    'admin.blog.cancel': 'Cancel',
    'admin.blog.config': 'Settings',
    'admin.blog.field.title': 'Article Title',
    'admin.blog.field.content': 'Content (Rich Text Editor)',
    'admin.blog.field.lang': 'Article Language',
    'admin.blog.field.slug': 'Slug (URL)',
    'admin.blog.field.cat': 'Category',
    'admin.blog.field.author': 'Author',
    'admin.blog.field.img': 'Cover Image URL',
    'admin.blog.publish': 'Publish Article',
    'admin.blog.publishing': 'Publishing...',
    'admin.blog.success.title': 'Article Published!',
    'admin.blog.success.desc': 'Redirecting to news section...',
    'admin.blog.denied.title': 'Access Denied',
    'admin.blog.denied.desc': 'Only administrators can access this page.',
    'admin.blog.denied.back': 'Back to Home',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    return (localStorage.getItem('language') as Language) || 'es';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
